import { useNavigate } from 'react-router-dom';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import type { ReleasePlan } from './ReleaseDialog';

/** The template that actually pushes tags and opens the gitops pull request.
 * The screens pick and preview; the template does the work, and its task
 * history is the audit record. */
const PROMOTE_TEMPLATE = 'template:default/fleet-promote';

/**
 * Starts a release from a resolved plan and follows the task.
 *
 * Shared by the system-wide page and the single-component card so there is one
 * place that knows the template's name and the shape of its input. Two callers
 * building that payload by hand would drift the moment either changed.
 */
export function useStartRelease(onStarted?: () => void) {
  const scaffolderApi = useApi(scaffolderApiRef);
  const alertApi = useApi(alertApiRef);
  const navigate = useNavigate();

  return async (plan: ReleasePlan) => {
    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: PROMOTE_TEMPLATE,
        values: {
          action: plan.action,
          environment: plan.environment,
          bump: plan.bump,
          dryRun: plan.dryRun,
          // The resolved plan travels with the request so the template acts on
          // exactly what was previewed, rather than recomputing and possibly
          // landing somewhere else.
          targets: plan.entries.map(entry => ({
            component: entry.service,
            workload: entry.component,
            from: entry.from,
            to: entry.to,
          })),
        },
      });
      onStarted?.();
      navigate(`/create/tasks/${taskId}`);
    } catch (error) {
      alertApi.post({
        message: `Could not start the release: ${(error as Error).message}`,
        severity: 'error',
      });
    }
  };
}
